import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Icon } from "@iconify/react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { toast } from "react-toastify";
import { FileDropzone } from "../../components/shared/FileDropzone";
import { Instructions } from "../../components/shared/Instructions";
import { SegmentedTabs } from "../../components/shared/SegmentedTabs";
import { ButtonPrimary, ButtonPrimary1 } from "../../components/utils";
import convertToBase64 from "../../utils/convertToBase64";
import { Grant } from "../../types/Grant";
import { useCompanyStore } from "../../store";
import { useValuationDetails } from "../../queries/Valuation";
import { useMultipleEmployeeGrants } from "../../queries/allocationMatrix";
import {
  AllocationMode,
  CostOrGain,
  QuantumMode,
  RuleScope,
} from "../../store/useAllocationMatrixStore";
import { formatCurrency, getCurrencyType } from "../../utils/currencyFormatter";
import { ValuationSource } from "../../types/ValuationType";
import { calculateAllocationValue } from "../../utils/allocationMatrixValue";
import { FormulaTooltip } from "./AllocationMatrixInfoCard";
import {
  useDownloadBulkRecommendationTemplate,
  useParseBulkRecommendationUpload,
  useSubmitBulkApproverActions,
} from "../../queries/grantApproval";

type RecommendationInputMode = "OPTIONS" | "VALUE";
type Recommendation = {
  inputMode: RecommendationInputMode;
  enteredValue: string;
  note: string;
};
type UploadPreviewRow = {
  rowNumber: number;
  identifier: string;
  grantId?: string;
  proposedOptions?: number;
  proposedValue?: number;
  inputMode?: RecommendationInputMode;
  optionsGranted?: number;
  valueOfOptions?: number;
  conversionSnapshot?: Record<string, any>;
  note?: string;
  errors: string[];
};

export function BulkRecommendChangeDialog({
  open,
  grants,
  onClose,
  embedded = false,
  managerScoped = false,
  onSubmitted,
  onEntryModeChange,
}: {
  open: boolean;
  grants: Grant[];
  onClose: () => void;
  /** Render the same recommendation workflow inside an existing bulk dialog. */
  embedded?: boolean;
  /** Use the manager-scoped valuation endpoint in the Manager Portal. */
  managerScoped?: boolean;
  onSubmitted?: () => void;
  onEntryModeChange?: (mode: "manual" | "upload") => void;
}) {
  const [entryMode, setEntryMode] = useState<"manual" | "upload">("manual");
  const [recommendations, setRecommendations] = useState<
    Record<string, Recommendation>
  >({});
  const [file, setFile] = useState<File>();
  const [showValidation, setShowValidation] = useState(false);
  const [uploadPreview, setUploadPreview] = useState<UploadPreviewRow[]>([]);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const uploadPreviewGridRef = useRef<AgGridReact>(null);
  const [selectedUploadGrantIds, setSelectedUploadGrantIds] = useState<
    string[]
  >([]);
  const { mutateAsync: submit, isLoading: isSubmitting } =
    useSubmitBulkApproverActions();
  const { mutateAsync: parseUpload, isLoading: isParsing } =
    useParseBulkRecommendationUpload();
  const { mutateAsync: downloadTemplate } =
    useDownloadBulkRecommendationTemplate();
  const currency = getCurrencyType();
  const { companyData } = useCompanyStore();
  const allocationMatrixEnabled = Boolean(
    companyData?.companySettings?.grantLetterSettings
      ?.useAllocationMatrixForGranting
  );
  const getDefaultInputMode = (grant: Grant): RecommendationInputMode =>
    (grant.matchedRule as any)?.allocationMode === AllocationMode.ABSOLUTE_VALUE
      ? "VALUE"
      : "OPTIONS";
  const emptyRecommendations = () =>
    grants.reduce<Record<string, Recommendation>>(
      (result, grant) => ({
        ...result,
        [grant.optionHolderId]: {
          inputMode: getDefaultInputMode(grant),
          enteredValue: "",
          note: "",
        },
      }),
      {}
    );
  const { data: valuationData, isPlaceholderData } =
    useValuationDetails(managerScoped);
  const uniqueEmployeeIds = useMemo(
    () => [...new Set(grants.map((grant) => grant.employeeId))],
    [grants]
  );
  const employeeGrantsQueries = useMultipleEmployeeGrants(
    uniqueEmployeeIds,
    managerScoped
  );
  const employeeGrantsById = useMemo(() => {
    const result: Record<string, Grant[]> = {};
    uniqueEmployeeIds.forEach((employeeId, index) => {
      result[employeeId] = employeeGrantsQueries[index]?.data || [];
    });
    return result;
  }, [employeeGrantsQueries, uniqueEmployeeIds]);
  const currentSharePrice = useMemo(() => {
    if (companyData?.companyMetaData.typeOfTheCompany === "PUBLIC") {
      return companyData?.companyMetaData?.pps || 0;
    }
    if (!isPlaceholderData && valuationData?.length) {
      const sortedValuations = [...valuationData].sort(
        (a: any, b: any) =>
          new Date(b.dateOfValuation).getTime() -
          new Date(a.dateOfValuation).getTime()
      );
      return (
        sortedValuations.find(
          (value: any) => value.valuationSource === ValuationSource.COMPANY
        )?.pps ||
        sortedValuations[0]?.pps ||
        0
      );
    }
    return 0;
  }, [companyData, isPlaceholderData, valuationData]);
  const allocatedByGrantId = useMemo(() => {
    const result: Record<string, number> = {};
    grants.forEach((grant) => {
      const rule = grant.matchedRule as any;
      const costOrGain = rule?.costOrGain as CostOrGain;
      result[grant.optionHolderId] = (
        employeeGrantsById[grant.employeeId] || []
      )
        .filter((item) => {
          const itemId = item.optionHolderId ?? item.id;
          return (
            item.optionHolderState !== "WITHDRAW" &&
            itemId !== grant.optionHolderId
          );
        })
        .reduce(
          (total, item) =>
            total +
            calculateAllocationValue({
              options: item.optionsGranted || 0,
              ratio: item.ratio,
              fmv: currentSharePrice,
              strikePrice: item.grantPrice || 0,
              allocationMode: rule?.allocationMode,
              costOrGain,
            }),
          0
        );
    });
    return result;
  }, [currentSharePrice, employeeGrantsById, grants]);

  const getGrantComparableValue = (grant: Grant) => {
    const rule = grant.matchedRule as any;
    return calculateAllocationValue({
      options: grant.optionsGranted || 0,
      ratio: grant.ratio,
      fmv: currentSharePrice,
      strikePrice: grant.grantPrice || 0,
      allocationMode: rule?.allocationMode,
      costOrGain: rule?.costOrGain,
    });
  };
  const getGrantValue = (grant: Grant) => {
    const rule = grant.matchedRule as any;
    return calculateAllocationValue({
      options: grant.optionsGranted || 0,
      ratio: grant.ratio,
      fmv: currentSharePrice,
      strikePrice: grant.grantPrice || 0,
      allocationMode: rule?.allocationMode ?? AllocationMode.ABSOLUTE_VALUE,
      costOrGain: rule?.costOrGain ?? CostOrGain.COST,
    });
  };
  const getRecommendationValue = (grant: Grant): number | null => {
    const rule = grant.matchedRule as any;
    const costOrGain = rule?.costOrGain ?? CostOrGain.COST;
    const strikePrice = grant.grantPrice || 0;

    if (
      currentSharePrice <= 0 ||
      (costOrGain === CostOrGain.GAIN && currentSharePrice <= strikePrice)
    ) {
      return null;
    }

    return calculateAllocationValue({
      options: grant.optionsGranted || 0,
      ratio: grant.ratio,
      fmv: currentSharePrice,
      strikePrice,
      allocationMode: AllocationMode.ABSOLUTE_VALUE,
      costOrGain,
    });
  };
  const formatRecommendationValue = (grant: Grant) => {
    const value = getRecommendationValue(grant);
    return value == null ? "—" : formatCurrency(value, currency);
  };
  const formatAllocationValue = (grant: Grant, value: number) =>
    (grant.matchedRule as any)?.allocationMode === AllocationMode.ABSOLUTE_VALUE
      ? formatCurrency(value, currency)
      : `${value.toLocaleString()} options`;
  const formatGrantValue = (grant: Grant) => {
    const rule = grant.matchedRule as any;
    const value = getGrantValue(grant);
    return rule?.allocationMode === AllocationMode.FIXED_OPTIONS
      ? `${value.toLocaleString()} options`
      : formatCurrency(value, currency);
  };
  const allocationFormulaRule = grants.find(
    (grant) =>
      (grant.matchedRule as any)?.allocationMode ===
      AllocationMode.ABSOLUTE_VALUE
  )?.matchedRule as any;
  const getLimitStatus = (grant: Grant) => {
    const rule = grant.matchedRule as any;
    if (!grant.allocationMatrixId || !rule) return null;
    const current = getGrantComparableValue(grant);
    const compared =
      rule.scope === RuleScope.PER_EMPLOYEE
        ? (allocatedByGrantId[grant.optionHolderId] || 0) + current
        : current;
    if (rule.quantumMax != null && compared > rule.quantumMax)
      return "Above limit";
    if (rule.quantumMin != null && compared < rule.quantumMin)
      return "Below limit";
    return "Within limit";
  };
  const formatRuleLimits = (grant: Grant, rule: any) => {
    if (rule?.quantumMode === QuantumMode.MIN) {
      return `Min: ${formatAllocationValue(grant, rule.quantumMin)}`;
    }
    if (rule?.quantumMode === QuantumMode.MAX) {
      return `Max: ${formatAllocationValue(grant, rule.quantumMax)}`;
    }
    return `${
      rule?.quantumMin != null
        ? formatAllocationValue(grant, rule.quantumMin)
        : "—"
    } – ${
      rule?.quantumMax != null
        ? formatAllocationValue(grant, rule.quantumMax)
        : "—"
    }`;
  };
  useEffect(() => {
    if (!open) return;
    setEntryMode("manual");
    setFile(undefined);
    setShowValidation(false);
    setUploadPreview([]);
    setUploadErrors([]);
    setSelectedUploadGrantIds([]);
    setRecommendations(emptyRecommendations());
    // The manager supplies a newly mapped grants array on each render. Resetting
    // on that reference would switch the user back to Manual Input mid-upload.
  }, [open]);

  const hasInvalidRecommendations = grants.some((grant) => {
    const value = recommendations[grant.optionHolderId]?.enteredValue?.trim();
    return !value || !Number.isFinite(Number(value)) || Number(value) < 0;
  });
  const hasUploadValidationErrors =
    uploadErrors.length > 0 ||
    uploadPreview.some((row) => row.errors.length > 0);
  const isUploadReadyToSubmit =
    Boolean(file) && uploadPreview.length > 0 && !hasUploadValidationErrors;
  const canSubmit =
    !isParsing &&
    !isSubmitting &&
    !hasInvalidRecommendations &&
    (entryMode === "manual" || isUploadReadyToSubmit);
  const canSubmitSelected =
    !isParsing &&
    !isSubmitting &&
    selectedUploadGrantIds.length > 0 &&
    selectedUploadGrantIds.every((grantId) => {
      const recommendation = recommendations[grantId]?.enteredValue?.trim();
      return (
        recommendation &&
        Number.isFinite(Number(recommendation)) &&
        Number(recommendation) >= 0
      );
    });

  const uploadPreviewColumnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: "Validity",
        width: 130,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        valueGetter: (p) => (p.data.errors.length ? "Invalid" : "Valid"),
        cellStyle: (p) => ({
          color: p.data.errors.length ? "#dc2626" : "#22c55e",
        }),
      },
      {
        headerName: "Grant Identifier",
        field: "identifier",
        minWidth: 180,
        editable: true,
      },
      {
        headerName: "Employee Name",
        minWidth: 160,
        valueGetter: (p) =>
          grants.find((g) => g.optionHolderId === p.data.grantId)
            ?.optionHolderName || "—",
      },
      {
        headerName: "No. of Options",
        minWidth: 150,
        valueGetter: (p) =>
          grants.find((g) => g.optionHolderId === p.data.grantId)
            ?.optionsGranted ?? "—",
      },
      {
        headerName: "Value of Options",
        minWidth: 180,
        valueGetter: (p) => {
          const grant = grants.find(
            (item) => item.optionHolderId === p.data.grantId
          );
          return grant ? formatRecommendationValue(grant) : "—";
        },
      },
      {
        headerName: "Recommended Options",
        field: "proposedOptions",
        minWidth: 170,
        editable: true,
        cellEditor: "agNumberCellEditor",
        cellEditorParams: { min: 0 },
      },
      {
        headerName: "Recommended Value",
        field: "proposedValue",
        minWidth: 170,
        editable: true,
        cellEditor: "agNumberCellEditor",
        cellEditorParams: { min: 0 },
      },
      { headerName: "Note", field: "note", minWidth: 200, editable: true },
    ],
    [grants, currentSharePrice, currency]
  );

  function updateRecommendation(
    grantId: string,
    field: "enteredValue" | "note",
    value: string
  ) {
    setRecommendations((current) => ({
      ...current,
      [grantId]: {
        inputMode: current[grantId]?.inputMode ?? "OPTIONS",
        enteredValue: current[grantId]?.enteredValue ?? "",
        note: current[grantId]?.note ?? "",
        [field]: value,
      },
    }));
  }

  function updateRecommendationMode(
    grantId: string,
    inputMode: RecommendationInputMode
  ) {
    setRecommendations((current) => ({
      ...current,
      [grantId]: {
        inputMode,
        enteredValue: "",
        note: current[grantId]?.note ?? "",
      },
    }));
  }

  function updateUploadPreviewRow(
    rowNumber: number,
    field: "identifier" | "proposedOptions" | "proposedValue" | "note",
    value: string
  ) {
    const nextRows = uploadPreview.map((row) => {
      if (row.rowNumber !== rowNumber) return row;
      const identifier = field === "identifier" ? value : row.identifier;
      const proposedOptionsValue =
        field === "proposedOptions"
          ? value
          : row.proposedOptions == null
          ? ""
          : String(row.proposedOptions);
      const proposedValueValue =
        field === "proposedValue"
          ? value
          : row.proposedValue == null
          ? ""
          : String(row.proposedValue);
      const proposedOptions = Number(proposedOptionsValue);
      const proposedValue = Number(proposedValueValue);
      const matchedGrant = grants.find(
        (grant) => grant.grantIdentifier === identifier.trim()
      );
      const errors: string[] = [];
      if (!identifier.trim()) errors.push("Grant Identifier is required");
      else if (!matchedGrant)
        errors.push("Grant identifier is not in the selected grants");
      if (!!proposedOptionsValue.trim() === !!proposedValueValue.trim())
        errors.push(
          "Enter exactly one of Recommended Options or Recommended Value"
        );
      if (
        proposedOptionsValue.trim() &&
        (!Number.isFinite(proposedOptions) || proposedOptions < 0)
      )
        errors.push("Recommended Options must be a non-negative number");
      if (
        proposedValueValue.trim() &&
        (!Number.isFinite(proposedValue) || proposedValue < 0)
      )
        errors.push("Recommended Value must be a non-negative number");
      return {
        ...row,
        identifier,
        proposedOptions:
          proposedOptionsValue.trim() && Number.isFinite(proposedOptions)
            ? proposedOptions
            : undefined,
        proposedValue:
          proposedValueValue.trim() && Number.isFinite(proposedValue)
            ? proposedValue
            : undefined,
        inputMode: proposedValueValue.trim()
          ? ("VALUE" as const)
          : ("OPTIONS" as const),
        note: field === "note" ? value : row.note,
        grantId: matchedGrant?.optionHolderId,
        errors,
      };
    });
    const validGrantIds = new Set(
      nextRows
        .filter((row) => !row.errors.length && row.grantId)
        .map((row) => row.grantId)
    );
    const nextErrors = grants
      .filter((grant) => !validGrantIds.has(grant.optionHolderId))
      .map((grant) => `Missing valid row for grant ${grant.grantIdentifier}`);
    setUploadPreview(nextRows);
    setUploadErrors(nextErrors);
    setRecommendations(
      nextRows.reduce<Record<string, Recommendation>>((result, row) => {
        const enteredValue =
          row.proposedValue != null ? row.proposedValue : row.proposedOptions;
        if (!row.grantId || row.errors.length || enteredValue == null)
          return result;
        return {
          ...result,
          [row.grantId]: {
            inputMode: row.proposedValue != null ? "VALUE" : "OPTIONS",
            enteredValue: String(enteredValue),
            note: row.note ?? "",
          },
        };
      }, {})
    );
  }

  function discardUploadPreview() {
    setFile(undefined);
    setUploadPreview([]);
    setUploadErrors([]);
    setRecommendations(emptyRecommendations());
    setShowValidation(false);
  }

  async function handleUpload(uploadedFile: File) {
    try {
      setFile(uploadedFile);
      const parsed = await parseUpload({
        file: (await convertToBase64(uploadedFile)) as string,
        grants: grants.map((grant) => ({
          id: grant.optionHolderId,
          identifier: grant.grantIdentifier,
        })),
      });
      setUploadPreview(parsed.rows);
      setUploadErrors(parsed.errors || []);
      setSelectedUploadGrantIds([]);
      setRecommendations(
        parsed.rows.reduce<Record<string, Recommendation>>((result, row) => {
          if (
            !row.grantId ||
            row.errors.length > 0 ||
            (row.proposedOptions == null && row.proposedValue == null)
          ) {
            return result;
          }
          return {
            ...result,
            [row.grantId]: {
              inputMode: row.proposedValue != null ? "VALUE" : "OPTIONS",
              enteredValue: String(
                row.proposedValue != null
                  ? row.proposedValue
                  : row.proposedOptions
              ),
              note: row.note ?? "",
            },
          };
        }, {})
      );
      const hasUploadErrors =
        parsed.errors.length > 0 ||
        parsed.rows.some((row) => row.errors.length > 0);
      setShowValidation(hasUploadErrors);
      if (hasUploadErrors) {
        toast("File loaded with validation errors. Review the preview.", {
          type: "warning",
          autoClose: 2500,
        });
      }
    } catch (error: any) {
      setFile(undefined);
      toast(error?.message || "Unable to parse the uploaded file.", {
        type: "error",
        autoClose: 2500,
      });
    }
  }

  async function handleDownloadTemplate() {
    try {
      const template = await downloadTemplate(
        grants.map((grant) => ({
          identifier: grant.grantIdentifier,
          employeeName: grant.optionHolderName,
          grantedOptions: grant.optionsGranted,
          currentValue: formatRecommendationValue(grant),
          allocated: grant.allocationMatrixId
            ? formatAllocationValue(
                grant,
                allocatedByGrantId[grant.optionHolderId] || 0
              )
            : "",
          matrixRule: grant.matchedRule
            ? `${grant.allocationMatrixName || "Allocation rule"} · ${
                (grant.matchedRule as any).scope === RuleScope.PER_EMPLOYEE
                  ? "Per employee"
                  : "Per grant"
              }: ${formatRuleLimits(grant, grant.matchedRule)}`
            : "",
          limitCheck: getLimitStatus(grant) || "",
        }))
      );
      const url = window.URL.createObjectURL(template);
      const link = document.createElement("a");
      link.href = url;
      link.download = "bulk-recommend-change-template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast(error?.message || "Unable to download the template.", {
        type: "error",
        autoClose: 2500,
      });
    }
  }

  async function handleSubmit(grantIds?: string[], closeAfterSubmit = true) {
    const targetGrants = grantIds
      ? grants.filter((grant) => grantIds.includes(grant.optionHolderId))
      : grants;
    const targetIsValid = targetGrants.every((grant) => {
      const value = recommendations[grant.optionHolderId]?.enteredValue?.trim();
      return value && Number.isFinite(Number(value)) && Number(value) >= 0;
    });
    if ((!grantIds && !canSubmit) || !targetGrants.length || !targetIsValid) {
      setShowValidation(true);
      return;
    }
    try {
      await submit(
        targetGrants.map((grant) => ({
          grantId: grant.optionHolderId,
          action: "RECOMMEND_CHANGE" as const,
          comment:
            recommendations[grant.optionHolderId]?.note.trim() || undefined,
          proposedChanges: {
            inputMode: recommendations[grant.optionHolderId].inputMode,
            enteredValue: Number(
              recommendations[grant.optionHolderId].enteredValue
            ),
          },
        }))
      );
      toast("Change recommendations submitted.", {
        type: "success",
        autoClose: 2000,
      });
      onSubmitted?.();
      if (closeAfterSubmit) onClose();
    } catch (error: any) {
      toast(error?.message || "Unable to submit recommendations.", {
        type: "error",
        autoClose: 2500,
      });
    }
  }

  const recommendationContent = (
    <>
      {entryMode === "manual" && (
        <p className="mb-4 text-sm text-gray-600">
          Propose a revised number of options or value for each selected grant.
          The recommendation defaults to the allocation rule&apos;s grant
          method. Notes are optional.
        </p>
      )}
      {allocationMatrixEnabled && (
        <div className="mb-4 flex items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-700">
          <Icon icon="ri:information-line" height={18} />
          Allocated values and limit checks use the current{" "}
          {companyData?.companyMetaData.typeOfTheCompany === "PUBLIC"
            ? "share price"
            : "valuation"}{" "}
          of {formatCurrency(currentSharePrice, currency)}.
        </div>
      )}
      <SegmentedTabs
        className="mb-6"
        activeTab={entryMode}
        onChange={(mode) => {
          setEntryMode(mode);
          onEntryModeChange?.(mode);
        }}
        tabs={[
          { id: "manual", label: "Manual Input" },
          { id: "upload", label: "File Upload" },
        ]}
      />

      {entryMode === "upload" ? (
        <div className="w-full space-y-6 pb-3">
          {uploadPreview.length > 0 && (
            <div className="ag-theme-material rounded-md border border-gray-200">
              <AgGridReact
                columnDefs={uploadPreviewColumnDefs}
                defaultColDef={{
                  sortable: true,
                  filter: true,
                  floatingFilter: true,
                  resizable: true,
                }}
                rowData={uploadPreview}
                ref={uploadPreviewGridRef}
                rowSelection="multiple"
                suppressRowClickSelection
                domLayout="autoHeight"
                onSelectionChanged={() =>
                  setSelectedUploadGrantIds(
                    (uploadPreviewGridRef.current?.api.getSelectedRows() || [])
                      .filter((row) => !row.errors.length && row.grantId)
                      .map((row) => row.grantId)
                  )
                }
                getRowId={(params) => String(params.data.rowNumber)}
                onCellValueChanged={(params) =>
                  updateUploadPreviewRow(
                    params.data.rowNumber,
                    params.colDef.field as
                      | "identifier"
                      | "proposedOptions"
                      | "proposedValue"
                      | "note",
                    String(params.newValue ?? "")
                  )
                }
              />
            </div>
          )}
          <div
            className={
              uploadPreview.length > 0
                ? "hidden"
                : "mx-auto w-full max-w-[760px] space-y-6"
            }
          >
            <div className="space-y-2 text-center">
              <h3 className="text-xl font-medium text-gray-900">Upload file</h3>
              <p className="text-sm text-gray-500">
                Download the prefilled template, complete it, then upload it
                here.
              </p>
              <button
                type="button"
                onClick={handleDownloadTemplate}
                className="text-sm font-medium text-primary hover:underline"
              >
                Download prefilled template
              </button>
            </div>
            <FileDropzone
              onFileSelect={handleUpload}
              onClear={() => {
                setFile(undefined);
                setRecommendations({});
                setUploadPreview([]);
                setUploadErrors([]);
                setSelectedUploadGrantIds([]);
              }}
              selectedFile={file}
              disabled={isParsing || isSubmitting}
              acceptedFileExtensions={[".xlsx"]}
              instructionText="Excel (.xlsx) with prefilled grant details. Enter either Recommended Options or Recommended Value, plus an optional Note."
            />
            {isParsing && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Icon icon="eos-icons:loading" className="animate-spin" />
                Validating file...
              </div>
            )}
            {uploadPreview.length > 0 && (
              <div className="overflow-x-auto rounded-md border border-gray-200">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Row</th>
                      <th className="px-3 py-2">Grant Identifier</th>
                      <th className="px-3 py-2">Recommended Options</th>
                      <th className="px-3 py-2">Recommended Value</th>
                      <th className="px-3 py-2">Note</th>
                      <th className="px-3 py-2">Validation</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {uploadPreview.map((row) => (
                      <tr key={row.rowNumber}>
                        <td className="px-3 py-2">{row.rowNumber}</td>
                        <td className="px-3 py-2">
                          <input
                            value={row.identifier}
                            onChange={(event) =>
                              updateUploadPreviewRow(
                                row.rowNumber,
                                "identifier",
                                event.target.value
                              )
                            }
                            className="w-full rounded border border-gray-200 px-2 py-1.5 outline-none focus:border-orange-400"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            value={row.proposedOptions ?? ""}
                            onChange={(event) =>
                              updateUploadPreviewRow(
                                row.rowNumber,
                                "proposedOptions",
                                event.target.value
                              )
                            }
                            className="w-full rounded border border-gray-200 px-2 py-1.5 outline-none focus:border-orange-400"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            value={row.proposedValue ?? ""}
                            onChange={(event) =>
                              updateUploadPreviewRow(
                                row.rowNumber,
                                "proposedValue",
                                event.target.value
                              )
                            }
                            className="w-full rounded border border-gray-200 px-2 py-1.5 outline-none focus:border-orange-400"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            value={row.note ?? ""}
                            onChange={(event) =>
                              updateUploadPreviewRow(
                                row.rowNumber,
                                "note",
                                event.target.value
                              )
                            }
                            className="w-full rounded border border-gray-200 px-2 py-1.5 outline-none focus:border-orange-400"
                          />
                        </td>
                        <td
                          className={`px-3 py-2 ${
                            row.errors.length
                              ? "text-red-600"
                              : "text-green-600"
                          }`}
                        >
                          {row.errors.length ? row.errors.join(", ") : "Valid"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {uploadErrors.length > 0 && (
                  <div className="border-t border-gray-200 px-3 py-2 text-sm text-red-600">
                    {uploadErrors.join("; ")}
                  </div>
                )}
              </div>
            )}
            <Instructions
              data={[
                "Download the prefilled template first",
                "Enter either Recommended Options or Recommended Value for every selected grant",
                "Note is optional",
                "The uploaded rows must match the selected grants exactly",
              ]}
            />
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-gray-200">
          <table className="w-full min-w-[1180px] text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-semibold">Grant identifier</th>
                <th className="px-4 py-3 font-semibold">Employee name</th>
                <th className="px-4 py-3 font-semibold">
                  <span className="inline-flex items-center gap-1">
                    No. of options
                    {allocationFormulaRule && (
                      <FormulaTooltip
                        costOrGain={
                          allocationFormulaRule.costOrGain ?? CostOrGain.COST
                        }
                      />
                    )}
                  </span>
                </th>
                {allocationMatrixEnabled && (
                  <>
                    <th className="px-4 py-3 font-semibold">Allocated</th>
                    <th className="px-4 py-3 font-semibold">Matrix rule</th>
                    <th className="px-4 py-3 font-semibold">Limit check</th>
                  </>
                )}
                <th className="required px-4 py-3 font-semibold">
                  Proposed change
                </th>
                <th className="px-4 py-3 font-semibold">Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {grants.map((grant) => {
                const recommendation = recommendations[grant.optionHolderId];
                const rule = grant.matchedRule as any;
                const limitStatus = getLimitStatus(grant);
                const invalid =
                  showValidation &&
                  (!recommendation?.enteredValue.trim() ||
                    !Number.isFinite(Number(recommendation.enteredValue)) ||
                    Number(recommendation.enteredValue) < 0);
                return (
                  <tr key={grant.optionHolderId}>
                    <td className="px-4 py-3 font-mono font-medium text-gray-800">
                      {grant.grantIdentifier}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {grant.optionHolderName}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      <div>{grant.optionsGranted.toLocaleString()}</div>
                      <div className="mt-0.5 text-xs text-gray-400">
                        Value: {formatGrantValue(grant)}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-400">
                        Strike:{" "}
                        {formatCurrency(grant.grantPrice || 0, currency)}
                      </div>
                    </td>
                    {allocationMatrixEnabled && (
                      <>
                        <td className="px-4 py-3 text-gray-700">
                          {grant.allocationMatrixId
                            ? formatAllocationValue(
                                grant,
                                allocatedByGrantId[grant.optionHolderId] || 0
                              )
                            : "—"}
                        </td>
                        <td className="px-4 py-3 text-xs leading-5 text-gray-700">
                          {rule ? (
                            <>
                              <div className="font-medium text-gray-800">
                                {grant.allocationMatrixName ||
                                  "Allocation rule"}
                              </div>
                              <div>
                                {rule.scope === RuleScope.PER_EMPLOYEE
                                  ? "Per employee"
                                  : "Per grant"}
                              </div>
                              <div className="text-gray-400">
                                {formatRuleLimits(grant, rule)}
                              </div>
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs font-medium">
                          {limitStatus ? (
                            <span
                              className={
                                limitStatus === "Within limit"
                                  ? "text-green-600"
                                  : "text-orange-600"
                              }
                            >
                              {limitStatus}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-2">
                      <div className="flex min-w-[250px] gap-2">
                        <select
                          aria-label={`Recommendation type for ${grant.grantIdentifier}`}
                          value={
                            recommendation?.inputMode ??
                            getDefaultInputMode(grant)
                          }
                          onChange={(event) =>
                            updateRecommendationMode(
                              grant.optionHolderId,
                              event.target.value as RecommendationInputMode
                            )
                          }
                          className="rounded border border-gray-200 bg-white px-2 text-sm text-gray-700 outline-none focus:border-orange-400"
                        >
                          <option value="OPTIONS">Options</option>
                          <option value="VALUE">Value</option>
                        </select>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={recommendation?.enteredValue ?? ""}
                          onChange={(event) =>
                            updateRecommendation(
                              grant.optionHolderId,
                              "enteredValue",
                              event.target.value
                            )
                          }
                          placeholder={
                            recommendation?.inputMode === "VALUE"
                              ? "Revised value"
                              : "Revised options"
                          }
                          className={`w-full rounded border px-2.5 py-2 text-sm outline-none focus:border-orange-400 ${
                            invalid ? "border-red-400" : "border-gray-200"
                          }`}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <input
                        value={recommendation?.note ?? ""}
                        onChange={(event) =>
                          updateRecommendation(
                            grant.optionHolderId,
                            "note",
                            event.target.value
                          )
                        }
                        placeholder="Optional note"
                        className="w-full rounded border border-gray-200 px-2.5 py-2 text-sm outline-none focus:border-orange-400"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {showValidation &&
        (hasInvalidRecommendations ||
          (entryMode === "upload" && !isUploadReadyToSubmit)) && (
          <p className="mt-3 text-sm text-red-600">
            Fix all validation errors before submitting the recommendation.
          </p>
        )}
    </>
  );

  if (embedded) {
    return (
      <div>
        {recommendationContent}
        <div className="mt-5 flex items-center justify-between gap-3">
          {entryMode === "upload" && uploadPreview.length > 0 && (
            <ButtonPrimary1 onClick={discardUploadPreview}>
              Discard
            </ButtonPrimary1>
          )}
          <div className="ml-auto flex gap-3">
            {entryMode === "upload" ? (
              <>
                <ButtonPrimary
                  disabled={!canSubmitSelected}
                  loading={isSubmitting || isParsing}
                  onClick={() => handleSubmit(selectedUploadGrantIds, false)}
                >
                  Submit selected
                </ButtonPrimary>
                <ButtonPrimary
                  disabled={!canSubmit}
                  loading={isSubmitting || isParsing}
                  onClick={() => handleSubmit(undefined, true)}
                >
                  Submit all
                </ButtonPrimary>
              </>
            ) : (
              <ButtonPrimary
                disabled={!canSubmit}
                loading={isSubmitting || isParsing}
                onClick={() => handleSubmit(undefined, true)}
              >
                Submit
              </ButtonPrimary>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={() => !isSubmitting && onClose()}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle className="flex items-center justify-between">
        <span className="text-base font-semibold">Recommend change</span>
        <IconButton
          aria-label="Close"
          disabled={isSubmitting}
          onClick={onClose}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>{recommendationContent}</DialogContent>
      <DialogActions className="min-h-[88px] border-t border-gray-200 px-8 py-5">
        {entryMode === "upload" && uploadPreview.length > 0 && (
          <ButtonPrimary1 className="mr-auto" onClick={discardUploadPreview}>
            Discard
          </ButtonPrimary1>
        )}
        <ButtonPrimary
          disabled={!canSubmit}
          loading={isSubmitting || isParsing}
          onClick={() => handleSubmit()}
        >
          Recommend change for {grants.length} grant
          {grants.length === 1 ? "" : "s"}
        </ButtonPrimary>
      </DialogActions>
    </Dialog>
  );
}
